import { useState, useEffect } from "react";
import { apiClient } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Loader2,
  Presentation,
  Download,
  ChevronLeft,
  ChevronRight,
  Image as ImageIcon,
  Plus,
  Check,
  LayoutGrid,
  BarChart2,
  Calendar,
  FolderOpen,
  Sparkles,
  ArrowLeft,
  FileText,
  Video,
  User,
  ShoppingBag,
  Palette,
  Stamp
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { formatDistanceToNow } from "date-fns";
import { cn } from "@/lib/utils";

interface PitchDeckGeneratorProps {
  projectId: string;
}

interface PitchDeckData {
  id?: string;
  _id?: string;
  project_id: string;
  target_audience: "producers" | "brands" | "investors";
  status: "queued" | "processing" | "completed" | "failed";
  progress: number;
  current_step?: string;
  sections?: {
    section_type: string;
    title: string;
    content: string;
    order: number;
  }[];
  media_assets?: {
    asset_id: string;
    filename: string;
    label: string;
    url: string;
    created_at: string;
    asset_type?: string;
  }[];
  selected_template?: string;
  updated_at: string;
  title?: string;
}

const ASSET_TYPES = [
  { id: 'logo', label: 'Project Logo', description: 'Replaces the logo in headers', icon: Stamp },
  { id: 'title_card', label: 'Title Background', description: 'Main cover image', icon: LayoutGrid },
  { id: 'team', label: 'Team Photo', description: 'For Creator Profile', icon: User },
  { id: 'character', label: 'Character Art', description: 'For Concept Overview', icon: Palette },
  { id: 'product', label: 'Product/Merch', description: 'For Brand Integration', icon: ShoppingBag },
  { id: 'other', label: 'General Asset', description: 'Smart fill for other slides', icon: ImageIcon },
];

export function PitchDeckGenerator({ projectId }: PitchDeckGeneratorProps) {
  const { toast } = useToast();
  const [deck, setDeck] = useState<PitchDeckData | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [audience, setAudience] = useState<"producers" | "brands" | "investors">("brands");
  const [templates, setTemplates] = useState<Record<string, any>>({});
  const [selectedSections, setSelectedSections] = useState<string[]>([]);
  const [exporting, setExporting] = useState<'ppt' | 'pdf' | null>(null);

  const SECTIONS_BY_AUDIENCE = {
    producers: [
      { id: "logline", label: "Logline" },
      { id: "concept_overview", label: "Concept Overview" },
      { id: "creator_profile", label: "Creator Profile" },
      { id: "audience_potential", label: "Audience Potential" },
      { id: "production_status", label: "Production Status" },
      { id: "comparable_projects", label: "Comparable Projects" },
      { id: "next_steps", label: "Next Steps" }
    ],
    brands: [
      { id: "logline", label: "Logline" },
      { id: "concept_overview", label: "Concept Overview" },
      { id: "creator_profile", label: "Creator Profile" },
      { id: "artie_score_summary", label: "Artie Score Summary" },
      { id: "brand_integration_opportunities", label: "Integration Opportunities" },
      { id: "investment_ask", label: "Partnership Proposal" }
    ],
    investors: [
      { id: "executive_summary", label: "Executive Summary" },
      { id: "market_opportunity", label: "Market Opportunity" },
      { id: "creator_track_record", label: "Creator Track Record" },
      { id: "financial_projections", label: "Financial Projections" },
      { id: "competitive_landscape", label: "Competitive Landscape" },
      { id: "monetization_strategy", label: "Monetization Strategy" },
      { id: "investment_ask", label: "Investment Ask" }
    ]
  };
  
  // Preview State
  const [currentPreviewSlide, setCurrentPreviewSlide] = useState(0);
  const [previewSlides, setPreviewSlides] = useState<any[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  
  // Asset Upload State
  const [uploading, setUploading] = useState(false);
  const [isAssetModalOpen, setIsAssetModalOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [selectedAssetType, setSelectedAssetType] = useState<string>('other');

  // Load Templates & Deck Status
  useEffect(() => {
    const loadData = async () => {
      try {
        const [tpls, deckData] = await Promise.all([
          apiClient.getPitchDeckTemplates(),
          apiClient.getPitchDeckByProject(projectId)
        ]);
        setTemplates(tpls);
        if (deckData) {
          const typedDeck = deckData as unknown as PitchDeckData;
          setDeck(typedDeck);
          if (typedDeck.target_audience) {
            setAudience(typedDeck.target_audience);
          }
          // Initialize selection from existing deck if available, else defaults
          if (typedDeck.sections && typedDeck.sections.length > 0) {
            setSelectedSections(typedDeck.sections.map(s => s.section_type));
          } else if (typedDeck.target_audience) {
             const audienceKey = typedDeck.target_audience as keyof typeof SECTIONS_BY_AUDIENCE;
             setSelectedSections(SECTIONS_BY_AUDIENCE[audienceKey].map(s => s.id));
          }
        }
      } catch (err) {
        console.error("Failed to load initial data", err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [projectId]);

  // Poll for status updates if processing
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (deck && (deck.status === "queued" || deck.status === "processing")) {
      interval = setInterval(async () => {
        try {
          const data = await apiClient.getPitchDeckByProject(projectId);
          if (data) setDeck(data as unknown as PitchDeckData);
        } catch (e) {
          console.error("Polling error", e);
        }
      }, 3000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [deck?.status, projectId]);

  // Auto-fetch preview when deck is ready
  useEffect(() => {
    if (deck && deck.status === "completed" && (deck.id || deck._id)) {
      loadPreview(deck.selected_template || 'clean_white');
    }
  }, [deck?.status, deck?.selected_template, deck?.updated_at]);

  const loadPreview = async (templateKey: string) => {
    if (!deck) return;
    const deckId = deck.id || deck._id;
    if (!deckId) return;

    setPreviewLoading(true);
    try {
      const slides = await apiClient.getPitchDeckPreview(deckId, templateKey);
      const mappedSlides = slides.map((s: any) => ({
        ...s,
        bg: s.bg_color
      }));
      setPreviewSlides(mappedSlides);
      
      // Preload images
      mappedSlides.forEach((slide: any) => {
        if ((slide.type === 'image' || slide.type === 'asset') && slide.url) {
          const img = new Image();
          img.src = slide.url;
        }
      });
    } catch (error) {
      console.error("Failed to load preview:", error);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      // Ensure we send the selected sections (even if empty, though UI implies selection)
      // This prevents the backend from falling back to defaults if we meant "only these few".
      const result = await apiClient.generatePitchDeck({
        project_id: projectId,
        target_audience: audience,
        include_sections: selectedSections
      });
      
      setDeck({
        id: result.id,
        project_id: projectId,
        target_audience: audience,
        status: "queued",
        progress: 0,
        current_step: "Queued for generation...",
        updated_at: new Date().toISOString()
      });
      
      toast({
        title: "Generation Started",
        description: "Artie is now crafting your pitch deck.",
      });
    } catch (error: any) {
      console.error("Failed to start generation:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to start generation",
        variant: "destructive",
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setPendingFile(file);
      setSelectedAssetType('other'); // Reset default
      setIsAssetModalOpen(true);
      
      // Reset the input so the same file can be selected again if needed
      e.target.value = '';
    }
  };

  const handleConfirmUpload = async () => {
    if (!pendingFile) return;
    setIsAssetModalOpen(false);
    await handleUploadAsset(pendingFile, selectedAssetType);
    setPendingFile(null);
  };

  const handleUploadAsset = async (file: File, type: string) => {
    if (!deck) return;
    const deckId = deck.id || deck._id;
    if (!deckId) return;

    setUploading(true);
    const defaultLabel = file.name.split('.')[0];

    try {
      await apiClient.uploadPitchDeckAsset(deckId, file, defaultLabel, type);
      toast({ title: "Asset Uploaded", description: "Image added to assets." });
      
      // Refresh deck
      const data = await apiClient.getPitchDeckByProject(projectId);
      if (data) {
        const typedDeck = data as unknown as PitchDeckData;
        setDeck(typedDeck);
        // Force refresh the preview to include the new asset immediately
        await loadPreview(typedDeck.selected_template || 'clean_white');
      }
    } catch (error: any) {
      toast({ title: "Upload Failed", description: error.message, variant: "destructive" });
    } finally {
      setUploading(false);
    }
  };

  const handleToggleSection = (sectionType: string) => {
    setSelectedSections(prev => {
      if (prev.includes(sectionType)) {
        return prev.filter(s => s !== sectionType);
      } else {
        // Maintain order based on default list
        const audienceKey = audience as keyof typeof SECTIONS_BY_AUDIENCE;
        const allSections = SECTIONS_BY_AUDIENCE[audienceKey].map(s => s.id);
        const newSet = new Set([...prev, sectionType]);
        return allSections.filter(id => newSet.has(id));
      }
    });
  };

  // Update selection defaults when audience changes (only if generating new)
  useEffect(() => {
     if (!deck || deck.status === 'queued') return;
     // If user manually switches tabs, reset selection to defaults for that tab
     // Note: This might be annoying if they customized it.
     // For now, we only sync if the deck audience matches.
  }, [audience]);

  const handleExport = async (format: 'ppt' | 'pdf') => {
    if (!deck) return;
    const deckId = deck.id || deck._id;
    if (!deckId) return;

    setExporting(format);
    try {
      const blob = await apiClient.exportPitchDeck(deckId, format);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Pitch-Deck-${deck.title || projectId}.${format === 'ppt' ? 'pptx' : 'pdf'}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();

      toast({
        title: "Export Successful",
        description: `Your ${format.toUpperCase()} file has been downloaded.`,
      });
    } catch (error: any) {
      toast({
        title: "Export Failed",
        description: error.message || `Could not export ${format.toUpperCase()}.`,
        variant: "destructive",
      });
    } finally {
      setExporting(null);
    }
  };

  // --- RENDER HELPERS ---

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-[var(--background)]">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--primary)]" />
      </div>
    );
  }

  // Empty State / Initial
  if (!deck) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-8 bg-[var(--background)]">
        <div className="w-24 h-24 relative mb-6">
          <div className="absolute inset-0 bg-[var(--secondary)] rounded-full blur-xl opacity-50"></div>
          {/* Use a placeholder or Artie icon */}
          <div className="w-full h-full rounded-full border-4 border-[var(--background)] shadow-xl relative z-10 bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
             <Sparkles className="w-10 h-10 text-white" />
          </div>
        </div>
        <h1 className="text-3xl font-semibold font-cabinet tracking-tight text-[var(--foreground)] mb-2">
          Pitch Deck Generator
        </h1>
        <p className="text-[var(--muted-foreground)] text-sm font-ibm max-w-md leading-relaxed mb-8">
          Create investor-grade presentations customized for your target partners.
        </p>
        <Button size="lg" onClick={handleGenerate} className="bg-[var(--foreground)] text-[var(--background)] hover:bg-[var(--foreground)]/90 rounded-2xl px-8 h-12 font-cabinet text-base shadow-lg hover:shadow-xl transition-all">
          Start New Deck
        </Button>
      </div>
    );
  }

  // Processing State
  if (deck.status === "queued" || deck.status === "processing") {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] bg-[var(--background)]">
        <div className="w-full max-w-md space-y-6 p-8 rounded-2xl border border-[var(--border)] bg-[var(--card)] shadow-sm">
          <div className="flex items-center justify-center">
             <Loader2 className="h-10 w-10 animate-spin text-[var(--primary)]" />
          </div>
          <div className="text-center space-y-2">
            <h3 className="text-xl font-semibold font-cabinet text-[var(--foreground)]">Crafting Your Deck</h3>
            <p className="text-[var(--muted-foreground)] text-sm font-ibm">{deck.current_step || "Analyzing project data..."}</p>
          </div>
          <div className="space-y-2">
            <Progress value={deck.progress} className="h-2 bg-[var(--secondary)]" />
            <div className="flex justify-between text-xs text-[var(--muted-foreground)] font-ibm">
              <span>{deck.progress}% Complete</span>
              <span>Est. 15-30s</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main Dashboard Interface
  return (
    <div className="flex flex-col w-full min-h-screen bg-[var(--background)] text-[var(--foreground)] font-ibm">
      

      {/* Top Nav */}
      <nav className="w-full max-w-[1600px] mx-auto px-6 py-6 flex items-center justify-between z-30 relative">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" className="w-10 h-10 rounded-xl border-[var(--border)] bg-white hover:bg-[var(--secondary)] shadow-sm" onClick={() => window.history.back()}>
            <ArrowLeft className="w-4 h-4 text-[var(--muted-foreground)]" />
          </Button>
          <div className="flex flex-col gap-0.5">
            <div className="flex items-center gap-2">
              <h2 className="font-cabinet font-bold text-[var(--foreground)] text-xl tracking-tight">
                {deck.title || "Pitch Deck"}
              </h2>
              <Badge variant="secondary" className="px-2 py-0.5 rounded-md text-[10px] bg-[var(--border)] text-[var(--muted-foreground)] font-ibm font-medium border-none uppercase tracking-wide">
                Pitch Deck
              </Badge>
            </div>
            <span className="text-[11px] text-[var(--muted-foreground)] font-ibm pl-0.5">
              Draft saved {formatDistanceToNow(new Date(deck.updated_at))} ago
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            className="h-10 px-4 rounded-xl text-sm font-medium text-[var(--foreground)] hover:bg-[var(--secondary)] bg-transparent gap-2"
            onClick={() => handleExport('ppt')}
            disabled={exporting === 'ppt'}
          >
            {exporting === 'ppt' ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            Export PPTX
          </Button>
          <Button
            className="h-10 px-6 rounded-xl text-sm font-medium bg-[#1A1A1A] text-white hover:bg-[#1A1A1A]/90 shadow-lg shadow-black/5 gap-2"
            onClick={() => handleExport('pdf')}
            disabled={exporting === 'pdf'}
          >
            {exporting === 'pdf' ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <FileText className="w-4 h-4" />
            )}
            Download PDF
          </Button>
        </div>
      </nav>

      {/* Main Content */}
      <main className="flex-1 z-10 w-full max-w-[1400px] mx-auto px-6 pb-20 flex flex-col gap-8">
        
        {/* Header Hero */}
        <div className="flex flex-col md:flex-row md:text-left gap-6 text-center max-w-4xl mx-auto mt-4 mb-10 items-center justify-center animate-slide-up">
          <div className="relative shrink-0 mr-4">
            <div className="w-24 h-24 rounded-full border-[6px] border-white shadow-xl relative z-10 overflow-hidden bg-gray-100">
               {/* Dog Avatar from Picture 1 */}
               <img
                 src="https://images.unsplash.com/photo-1518020382113-a7e8fc38eac9?q=80&w=400&auto=format&fit=crop"
                 alt="Artie"
                 className="w-full h-full object-cover"
               />
            </div>
            {/* Orange Badge */}
            <div className="absolute -right-8 -top-3 -rotate-6 bg-[#FFA500] text-black text-[10px] font-bold px-3 py-1.5 rounded-lg shadow-md z-20 border-2 border-white whitespace-nowrap font-ibm uppercase tracking-wider flex items-center gap-1">
              Ready to help! 🚀
            </div>
          </div>
          <div className="flex flex-col gap-2 md:items-start items-center">
            <h1 className="text-4xl md:text-5xl font-bold font-cabinet tracking-tight text-[#1A1A1A] leading-tight">
              Pitch Deck Generator
            </h1>
            <p className="text-[var(--muted-foreground)] text-sm font-ibm max-w-lg leading-relaxed text-left">
              Create investor-grade presentations customized for your target partners.
            </p>
          </div>
        </div>

        {/* 1. Live Preview Section */}
        <div className="w-full max-w-5xl mx-auto flex flex-col gap-4 animate-slide-up delay-100">
          <div className="flex justify-between items-center px-1">
            <h3 className="text-sm font-semibold text-[var(--foreground)]">Live Preview</h3>
            <div className="flex gap-2 text-[var(--muted-foreground)]">
              <button 
                onClick={() => setCurrentPreviewSlide(Math.max(0, currentPreviewSlide - 1))}
                disabled={currentPreviewSlide === 0}
                className="p-1.5 hover:bg-[var(--secondary)] rounded-md transition-colors disabled:opacity-30"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-xs font-ibm self-center min-w-[3rem] text-center">
                {previewSlides.length > 0 ? currentPreviewSlide + 1 : 0} / {previewSlides.length}
              </span>
              <button 
                onClick={() => setCurrentPreviewSlide(Math.min(previewSlides.length - 1, currentPreviewSlide + 1))}
                disabled={currentPreviewSlide === previewSlides.length - 1}
                className="p-1.5 hover:bg-[var(--secondary)] rounded-md transition-colors disabled:opacity-30"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Main Slide Canvas */}
          <div className="w-full aspect-video bg-[#1a1a1a] rounded-xl shadow-2xl border border-[var(--border)] overflow-hidden relative group">
            {previewLoading ? (
              <div className="absolute inset-0 flex items-center justify-center text-white/50">
                <Loader2 className="w-8 h-8 animate-spin" />
              </div>
            ) : previewSlides.length > 0 ? (
              <div 
                className="w-full h-full flex flex-col relative transition-opacity duration-300"
                style={{
                  backgroundColor: previewSlides[currentPreviewSlide]?.bg ? `rgb(${previewSlides[currentPreviewSlide].bg.join(',')})` : '#fff'
                }}
              >
                {/* --- Slide Renderer Logic (Reused from previous implementation) --- */}
                {(() => {
                  const slide = previewSlides[currentPreviewSlide];
                  if (!slide) return null;
                  
                  // Simple logic to detect dark bg based on template name or manually
                  const isDark = deck.selected_template === 'dark_cinematic';
                  const textColor = isDark ? 'text-white' : 'text-slate-900';
                  const mutedTextColor = isDark ? 'text-white/60' : 'text-slate-500';

                  if (slide.type === 'title' || slide.type === 'title_slide') {
                    return (
                      <div className="h-full flex flex-col relative p-12 justify-between overflow-hidden">
                        {/* Background Image & Overlay */}
                        <div className="absolute inset-0 z-0">
                           <img
                             src="https://images.unsplash.com/photo-1514933651103-005eec06c04b?q=80&w=1200&auto=format&fit=crop"
                             className="w-full h-full object-cover"
                             alt="Cover"
                           />
                           <div className="absolute inset-0 bg-black/60 backdrop-blur-[2px]"></div>
                        </div>

                        {/* Top: Brand Tag */}
                        <div className="flex justify-between items-start w-full z-10 relative">
                           <div className="w-10 h-10 bg-white text-black rounded-full flex items-center justify-center font-cabinet font-bold text-lg shadow-sm">A</div>
                           <div className="text-right text-white">
                             <span className="block text-[10px] uppercase tracking-widest font-ibm opacity-80">Brand Partnership</span>
                             <span className="block text-xs font-semibold mt-0.5">Proposal</span>
                           </div>
                        </div>

                        {/* Center: Title */}
                        <div className="max-w-xl z-10 relative text-white">
                          <h1 className="text-5xl font-bold font-cabinet tracking-tight leading-tight mb-4">{slide.title}</h1>
                          <div className="h-1 w-16 mb-6 rounded-full bg-[var(--primary)]" />
                          <p className="text-base font-light font-ibm leading-relaxed opacity-90">{slide.subtitle}</p>
                        </div>

                        {/* Bottom: Metrics Strip */}
                        {slide.metrics && slide.metrics.length > 0 ? (
                          <div className="grid grid-cols-3 gap-8 pt-6 border-t border-white/20 w-full z-10 relative text-white">
                             {slide.metrics.map((m: any, i: number) => (
                               <div key={i}>
                                 <span className={cn("block text-2xl font-cabinet font-bold", i === 0 ? "text-[var(--primary)]" : "text-white")}>{m.value}</span>
                                 <span className="text-[9px] uppercase tracking-wider opacity-60 font-ibm text-white">{m.label}</span>
                               </div>
                             ))}
                          </div>
                        ) : (
                          /* Placeholder Metrics if none returned (Matches design mock) */
                          <div className="grid grid-cols-3 gap-8 pt-6 border-t border-white/20 w-full z-10 relative text-white">
                             <div>
                               <span className="block text-2xl font-cabinet font-bold text-[var(--primary)]">1.2M</span>
                               <span className="text-[9px] uppercase tracking-wider opacity-60 font-ibm text-white">Projected Reach</span>
                             </div>
                             <div>
                               <span className="block text-2xl font-cabinet font-bold text-white">94%</span>
                               <span className="text-[9px] uppercase tracking-wider opacity-60 font-ibm text-white">Brand Alignment</span>
                             </div>
                             <div>
                               <span className="block text-2xl font-cabinet font-bold text-white">Gen Z</span>
                               <span className="text-[9px] uppercase tracking-wider opacity-60 font-ibm text-white">Target Demo</span>
                             </div>
                          </div>
                        )}
                      </div>
                    );
                  }
                  
                  if (slide.type === 'section' || slide.type === 'content') {
                    return (
                      <div className="h-full flex flex-col p-10 justify-center">
                        <div className="flex items-center gap-3 mb-4">
                          <div className={cn("h-6 w-1.5 rounded-full", isDark ? "bg-[var(--primary)]" : "bg-indigo-600")} />
                          <h2 className={cn("text-xl font-bold font-cabinet tracking-tight", textColor)}>{slide.title}</h2>
                        </div>
                        <div className={cn("text-base leading-relaxed whitespace-pre-wrap font-ibm", textColor, "opacity-90")}>
                          {slide.content}
                        </div>
                      </div>
                    );
                  }
                  
                  if (slide.type === 'image' || slide.type === 'asset') {
                    return (
                      <div className="h-full w-full relative bg-slate-100">
                        {slide.url ? (
                          <img
                            src={slide.url}
                            alt={slide.label}
                            className="w-full h-full object-cover"
                            onError={(e) => (e.currentTarget.style.display = 'none')}
                          />
                        ) : (
                          <div className="absolute inset-0 flex flex-col items-center justify-center text-[var(--muted-foreground)]">
                            <ImageIcon className="w-12 h-12 mb-2 opacity-20" />
                            <span className="text-sm font-medium">Image Placeholder</span>
                          </div>
                        )}
                        {/* Overlay text if needed */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent pointer-events-none flex items-end p-8">
                           <div>
                             <Badge className="bg-white/90 text-black mb-2 hover:bg-white border-none">Asset</Badge>
                             <h3 className="text-2xl font-bold text-white font-cabinet">{slide.label}</h3>
                           </div>
                        </div>
                      </div>
                    );
                  }

                  if (slide.type === 'image_placeholder') {
                    return (
                      <div className="h-full w-full flex flex-col items-center justify-center p-6 bg-[var(--secondary)]/50">
                        <div className="w-full h-full border-2 border-dashed border-[var(--border)] rounded-lg flex flex-col items-center justify-center">
                          <ImageIcon className="h-10 w-10 mb-3 text-[var(--muted-foreground)] opacity-50" />
                          <p className="text-sm font-medium text-[var(--muted-foreground)]">Insert "{slide.label}"</p>
                        </div>
                      </div>
                    );
                  }
                  
                  return (
                    <div className="h-full flex items-center justify-center text-muted-foreground">
                      Unsupported Slide Type
                    </div>
                  );
                })()}
              </div>
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-white/50">
                No slides available
              </div>
            )}
          </div>

          {/* Thumbnails Strip */}
          <div className="grid grid-cols-6 md:grid-cols-8 gap-3 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-[var(--border)]">
            {previewSlides.map((slide, idx) => (
              <div 
                key={idx}
                onClick={() => setCurrentPreviewSlide(idx)}
                className={cn(
                  "aspect-video rounded-lg border relative overflow-hidden cursor-pointer transition-all hover:border-[var(--primary)]/50",
                  currentPreviewSlide === idx ? "border-[var(--primary)] ring-2 ring-[var(--primary)]/20" : "border-[var(--border)] bg-[var(--card)]"
                )}
              >
                {/* Mini Preview Mockup */}
                <div 
                  className="w-full h-full flex flex-col p-1 gap-0.5"
                  style={{ backgroundColor: slide.bg ? `rgb(${slide.bg.join(',')})` : '#fff' }}
                >
                   {slide.type.includes('image') || slide.type.includes('asset') ? (
                      slide.url ? <img src={slide.url} className="w-full h-full object-cover" /> : <div className="w-full h-full bg-[var(--secondary)]" />
                   ) : (
                      <>
                        <div className="w-1/2 h-1 bg-current opacity-20 rounded-full mb-1" />
                        <div className="w-full flex-1 bg-[var(--secondary)]/50 rounded-sm" />
                      </>
                   )}
                </div>
                {/* Index Badge */}
                <div className="absolute bottom-0.5 right-0.5 text-[8px] font-bold px-1 bg-black/50 text-white rounded">
                  {idx + 1}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="w-full max-w-5xl mx-auto h-[1px] bg-[var(--border)] opacity-50"></div>

        {/* 2. Options & Config (Grid Layout) */}
        <div className="w-full max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6 animate-slide-up delay-200">
          
          {/* Col 1: Strategy & Brand */}
          <div className="flex flex-col gap-5">
            {/* Deck Strategy */}
            <div className="bg-[var(--card)] border border-[var(--border)] rounded-2xl p-4 shadow-sm">
              <label className="text-xs font-semibold text-[var(--muted-foreground)] uppercase tracking-wider font-ibm mb-3 block">
                Deck Strategy
              </label>
              <div className="flex bg-[var(--secondary)] p-1 rounded-xl">
                <button 
                  onClick={() => setAudience('brands')}
                  className={cn(
                    "flex-1 py-2 px-3 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-all",
                    audience === 'brands' 
                      ? "bg-[var(--background)] shadow-sm text-[var(--foreground)]" 
                      : "text-[var(--muted-foreground)] hover:text-[var(--foreground)]"
                  )}
                >
                  <Sparkles className={cn("w-4 h-4", audience === 'brands' ? "text-[var(--primary)]" : "")} />
                  Brand Partner
                </button>
                <button 
                  onClick={() => setAudience('producers')}
                  className={cn(
                    "flex-1 py-2 px-3 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-all",
                    audience === 'producers'
                      ? "bg-[var(--background)] shadow-sm text-[var(--foreground)]"
                      : "text-[var(--muted-foreground)] hover:text-[var(--foreground)]"
                  )}
                >
                  <Video className={cn("w-4 h-4", audience === 'producers' ? "text-[var(--primary)]" : "")} />
                  Producer
                </button>
              </div>
            </div>

            {/* Target Brand (Input) */}
            <div className="bg-[var(--card)] border border-[var(--border)] rounded-2xl p-5 shadow-sm flex flex-col h-full">
              <div className="flex justify-between items-center mb-4">
                <label className="text-xs font-semibold text-[var(--muted-foreground)] uppercase tracking-wider font-ibm">Target Brand / Studio</label>
              </div>
              <Input 
                placeholder="e.g. Nike, Red Bull, Netflix" 
                className="bg-[var(--secondary)] border-[var(--border)] rounded-xl px-4 py-6 text-sm focus-visible:ring-[var(--primary)]"
              />
              <p className="text-[10px] text-[var(--muted-foreground)] mt-3 leading-relaxed">
                Artie will adjust the tone and visual style of the deck to match the brand's archetype.
              </p>
            </div>
          </div>

          {/* Col 2: Included Sections */}
          <div className="bg-[var(--card)] border border-[var(--border)] rounded-2xl p-5 shadow-sm flex flex-col">
            <label className="text-xs font-semibold text-[var(--muted-foreground)] uppercase tracking-wider font-ibm mb-4 block">
              Included Sections
            </label>
            <div className="flex flex-col gap-2 flex-1 max-h-[300px] overflow-y-auto custom-scrollbar">
              
              {SECTIONS_BY_AUDIENCE[audience as keyof typeof SECTIONS_BY_AUDIENCE]?.map((section) => {
                 const isSelected = selectedSections.includes(section.id);
                 return (
                  <label
                    key={section.id}
                    className="flex items-center justify-between group cursor-pointer p-2 -mx-2 hover:bg-[var(--secondary)] rounded-lg transition-colors"
                    onClick={(e) => { e.preventDefault(); handleToggleSection(section.id); }}
                  >
                    <div className="flex items-center gap-3">
                      <div className={cn(
                        "w-4 h-4 rounded border flex items-center justify-center transition-colors",
                        isSelected ? "border-[var(--primary)] bg-[var(--primary)]" : "border-[var(--muted-foreground)] bg-transparent"
                      )}>
                          {isSelected && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <span className="text-sm font-medium text-[var(--foreground)]">{section.label}</span>
                    </div>
                    <div className={cn("w-1.5 h-1.5 rounded-full", isSelected ? "bg-green-500" : "bg-gray-300")} />
                  </label>
                 );
              })}
            </div>
          </div>

          {/* Col 3: Visual Assets & Action */}
          <div className="flex flex-col gap-5">
            {/* Assets Selector */}
            <div className="bg-[var(--card)] border border-[var(--border)] rounded-2xl p-5 shadow-sm flex-1">
              <div className="flex justify-between items-center mb-3">
                <label className="text-xs font-semibold text-[var(--muted-foreground)] uppercase tracking-wider font-ibm">Visual Assets</label>
                <span className="text-[10px] text-[var(--primary)] cursor-pointer hover:underline font-medium">View All</span>
              </div>
              
              <div className="grid grid-cols-4 gap-2">
                {/* Upload Button */}
                <div className="relative">
                   <Input
                      id="asset-upload"
                      type="file"
                      accept="image/*"
                      onChange={handleFileSelect}
                      className="hidden"
                      disabled={uploading}
                    />
                   <button
                      onClick={() => document.getElementById('asset-upload')?.click()}
                      disabled={uploading}
                      className="w-full aspect-square rounded-xl border border-dashed border-[var(--border)] hover:border-[var(--primary)] hover:bg-[var(--secondary)] transition-all flex flex-col items-center justify-center gap-1 text-[var(--muted-foreground)] group"
                    >
                      {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 group-hover:scale-110 transition-transform" />}
                      <span className="text-[9px] font-medium">Add</span>
                   </button>
                </div>

                {/* Asset Thumbnails */}
                {deck.media_assets && deck.media_assets.length > 0 && deck.media_assets.slice(0, 7).map((asset) => (
                  <div key={asset.asset_id} className="aspect-square rounded-xl bg-[var(--secondary)] relative overflow-hidden group border border-[var(--border)]">
                    <img src={asset.url} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" alt={asset.label} />
                    
                    {/* Asset Type Badge */}
                    <div className="absolute bottom-0 left-0 right-0 bg-black/60 p-1 backdrop-blur-[1px]">
                      <p className="text-[8px] text-white text-center font-medium truncate capitalize">
                        {ASSET_TYPES.find(t => t.id === asset.asset_type)?.label || 'Asset'}
                      </p>
                    </div>

                    <div className="absolute top-1 right-1 w-4 h-4 bg-[var(--primary)] rounded-full flex items-center justify-center text-[var(--primary-foreground)] shadow-sm opacity-0 group-hover:opacity-100 transition-opacity">
                      <Check className="w-2.5 h-2.5" />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Asset Type Modal */}
            <Dialog open={isAssetModalOpen} onOpenChange={setIsAssetModalOpen}>
              <DialogContent className="sm:max-w-md bg-[var(--card)] border-[var(--border)]">
                <DialogHeader>
                  <DialogTitle className="font-cabinet text-xl">Select Asset Type</DialogTitle>
                  <DialogDescription className="font-ibm">
                    Choose how this image should be used in your pitch deck.
                  </DialogDescription>
                </DialogHeader>
                
                <div className="grid gap-4 py-4">
                  <RadioGroup value={selectedAssetType} onValueChange={setSelectedAssetType} className="grid grid-cols-2 gap-3">
                    {ASSET_TYPES.map((type) => {
                      const Icon = type.icon;
                      return (
                        <div key={type.id}>
                          <RadioGroupItem value={type.id} id={type.id} className="peer sr-only" />
                          <Label
                            htmlFor={type.id}
                            className="flex flex-col items-center justify-between rounded-xl border-2 border-[var(--border)] bg-[var(--secondary)]/50 p-4 hover:bg-[var(--secondary)] hover:text-accent-foreground peer-data-[state=checked]:border-[var(--primary)] peer-data-[state=checked]:bg-[var(--primary)]/5 [&:has([data-state=checked])]:border-[var(--primary)] cursor-pointer h-full transition-all"
                          >
                            <Icon className="mb-3 h-6 w-6 text-[var(--muted-foreground)] peer-data-[state=checked]:text-[var(--primary)]" />
                            <div className="space-y-1 text-center">
                              <div className="font-semibold leading-none">{type.label}</div>
                              <div className="text-[10px] text-[var(--muted-foreground)] leading-tight">{type.description}</div>
                            </div>
                          </Label>
                        </div>
                      )
                    })}
                  </RadioGroup>
                </div>

                <DialogFooter className="sm:justify-between gap-2">
                  <div className="flex items-center gap-2 text-xs text-[var(--muted-foreground)] truncate max-w-[200px]">
                    {pendingFile && (
                      <>
                        <ImageIcon className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">{pendingFile.name}</span>
                      </>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button variant="ghost" onClick={() => setIsAssetModalOpen(false)}>Cancel</Button>
                    <Button onClick={handleConfirmUpload} disabled={uploading}>
                      {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      Upload Asset
                    </Button>
                  </div>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Regenerate Action */}
            <Button 
              size="lg" 
              onClick={handleGenerate} 
              disabled={generating}
              className="w-full py-8 bg-[var(--foreground)] text-[var(--background)] rounded-2xl font-semibold font-cabinet text-lg hover:shadow-2xl hover:-translate-y-0.5 transition-all flex items-center justify-center gap-3 group relative overflow-hidden"
            >
              {generating ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]"></div>
                  <Sparkles className="w-5 h-5" />
                  Regenerate Deck
                </>
              )}
            </Button>
          </div>

        </div>

      </main>
    </div>
  );
}